import { useRegisterSW } from "virtual:pwa-register/react";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { playSpeakeasyChime } from "../lib/audio";
import { adminGetAppSettings } from "../lib/api";
import { nextCheckDelay, shouldApplyMandatoryReload } from "../lib/mandatoryReload";

const LAST_CHECK_KEY = "station.last_mandatory_check";
const LAST_APPLIED_KEY = "station.last_mandatory_reload_id";

function readStore(store: () => Storage, key: string): string | null {
  try {
    return store().getItem(key);
  } catch {
    return null;
  }
}
function writeStore(store: () => Storage, key: string, value: string) {
  try {
    store().setItem(key, value);
  } catch {
    /* private mode / blocked storage: such a tablet has no stored session either, so its settings
       check fails and it never force-reloads; it still gets the normal update prompt */
  }
}

/**
 * UpdatePrompt — High-visibility eye-catching banner that appears at the top
 * of the screen when a new service worker / app version is ready.
 *
 * Features:
 * - Top-center placement (top: 16px, zIndex: 10050) above all navigation & modals.
 * - Glowing gold border with breathing pulse animation.
 * - Gentle audio chime cue on arrival so staff hear that a deploy has landed.
 * - Prominent 1-tap "✨ Reload Now" button.
 * - "Later (10m)" snooze button that collapses into a persistent top-right mini-pill
 *   with an auto-re-prompt timer so updates are never forgotten.
 */
export function UpdatePrompt() {
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const [isSnoozed, setIsSnoozed] = useState(false);
  const chimePlayedRef = useRef(false);
  const reloadCheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      registrationRef.current = registration;
      // Poll for a new SW every 60s so tablets left open all service catch deploys.
      setInterval(() => {
        registration.update();
      }, 60_000);
    },
    onOfflineReady() {
      console.info("[Station] PWA ready — available offline.");
    },
  });

  // Play audio chime when a new version is detected
  useEffect(() => {
    if (needRefresh && !chimePlayedRef.current) {
      chimePlayedRef.current = true;
      try {
        playSpeakeasyChime("success");
      } catch {}
    }
  }, [needRefresh]);

  // Handle Snooze Timer (Auto-re-expand after 10 minutes)
  const handleSnooze = () => {
    setIsSnoozed(true);
    setTimeout(() => {
      setIsSnoozed(false);
    }, 10 * 60 * 1000);
  };

  // Admin-triggered "everyone check NOW" & "mandatory reload" broadcasts.
  //
  // The channel is public (anyone with the publishable key can send on it), so a
  // `mandatory_reload` event is only a hint: the tablet asks the server whether a manager really
  // sent one (security plan phase 4, lib/mandatoryReload.ts). Events inside the check window are
  // coalesced into one trailing check, never dropped, so forged events cannot hide a real one.
  // `force_update_check` stays unverified: it only asks the service worker to look for a new
  // build, which the 60 s poll does anyway, and shows the normal prompt if there is one.
  useEffect(() => {
    let cancelled = false;

    const applyReload = () => {
      const now = Date.now();
      const lastReload = Number(readStore(() => sessionStorage, "station.last_mandatory_reload") || 0);
      if (now - lastReload < 30_000) {
        console.warn("[Station] Mandatory reload ignored due to active cooldown.");
        return;
      }
      writeStore(() => sessionStorage, "station.last_mandatory_reload", String(now));
      console.info("[Station] Verified mandatory reload — applying new version.");

      if (registrationRef.current) {
        registrationRef.current
          .update()
          .then(() => {
            updateServiceWorker(true);
          })
          .catch(() => {
            window.location.reload();
          });
      } else {
        window.location.reload();
      }
    };

    const verifyAndReload = async () => {
      reloadCheckTimerRef.current = null;
      if (cancelled) return;
      writeStore(() => sessionStorage, LAST_CHECK_KEY, String(Date.now()));
      let res: Awaited<ReturnType<typeof adminGetAppSettings>>;
      try {
        res = await adminGetAppSettings();
      } catch (err) {
        console.warn("[Station] Mandatory reload not verified (settings check failed) — ignored.", err);
        return;
      }
      if (cancelled) return;
      const marker = res.last_mandatory_reload ?? null;
      if (!shouldApplyMandatoryReload(marker, res.server_now, readStore(() => localStorage, LAST_APPLIED_KEY))) {
        console.warn("[Station] Mandatory reload broadcast has no fresh server record — ignored.");
        return;
      }
      writeStore(() => localStorage, LAST_APPLIED_KEY, marker!.id);
      applyReload();
    };

    const scheduleCheck = () => {
      if (reloadCheckTimerRef.current) return; // a check is already queued; this event joins it
      const lastCheck = Number(readStore(() => sessionStorage, LAST_CHECK_KEY) || 0);
      reloadCheckTimerRef.current = setTimeout(() => {
        void verifyAndReload();
      }, nextCheckDelay(lastCheck, Date.now()));
    };

    import("../lib/realtime").then(
      ({ getBroadcastChannel, BROADCAST_EVENT_FORCE_UPDATE, BROADCAST_EVENT_MANDATORY_RELOAD }) => {
        if (cancelled) return;
        const ch = getBroadcastChannel();
        ch.on("broadcast", { event: BROADCAST_EVENT_FORCE_UPDATE }, () => {
          registrationRef.current?.update().catch(() => {});
        });
        ch.on("broadcast", { event: BROADCAST_EVENT_MANDATORY_RELOAD }, () => {
          scheduleCheck();
        });
        ch.subscribe();
      }
    );
    return () => {
      cancelled = true;
      if (reloadCheckTimerRef.current) {
        clearTimeout(reloadCheckTimerRef.current);
        reloadCheckTimerRef.current = null;
      }
    };
  }, [updateServiceWorker]);

  if (!needRefresh) return null;

  return (
    <>
      <style>{`
        @keyframes stationPulseGlow {
          0% {
            box-shadow: 0 8px 30px rgba(0,0,0,0.85), 0 0 15px rgba(249, 175, 54, 0.4);
            border-color: rgba(249, 175, 54, 0.6);
          }
          50% {
            box-shadow: 0 12px 40px rgba(0,0,0,0.95), 0 0 28px rgba(249, 175, 54, 0.85);
            border-color: rgba(249, 175, 54, 0.95);
          }
          100% {
            box-shadow: 0 8px 30px rgba(0,0,0,0.85), 0 0 15px rgba(249, 175, 54, 0.4);
            border-color: rgba(249, 175, 54, 0.6);
          }
        }
        @keyframes stationRocketBob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px) scale(1.1); }
        }
        @keyframes stationDotPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.35); opacity: 0.6; }
        }
      `}</style>

      {isSnoozed ? (
        /* Minimized Persistent Pill (Top Right) */
        <aside
          style={styles.snoozePill}
          onClick={() => setIsSnoozed(false)}
          title="New Station version waiting — click to view & reload"
          role="status"
        >
          <span style={styles.snoozeDot} />
          <span style={{ fontWeight: 800, color: "var(--gold, #F9AF36)" }}>⚡ Update Ready</span>
          <span style={styles.snoozeHint}>· Tap to open</span>
        </aside>
      ) : (
        /* Full High-Visibility Top Banner */
        <aside style={styles.bannerWrap} role="alert" aria-live="assertive">
          <div style={styles.bannerCard}>
            <div style={styles.iconCircle}>
              <span style={styles.rocketIcon}>🚀</span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1, minWidth: 0 }}>
              <div style={styles.contentBlock}>
                <div style={styles.headerRow}>
                  <span style={styles.badge}>APP UPDATE READY</span>
                  <span style={styles.titleText}>New Station Version Live</span>
                </div>
                <p style={styles.subtitleText}>
                  Fresh recipes, speed fixes & improvements have been deployed.
                </p>
              </div>

              <div style={styles.actions}>
                <button
                  type="button"
                  style={styles.laterBtn}
                  onClick={handleSnooze}
                  title="Snooze alert for 10 minutes"
                >
                  Later (10m)
                </button>
                <button
                  type="button"
                  style={styles.reloadBtn}
                  onClick={() => updateServiceWorker(true)}
                  title="Reload Station now with latest updates"
                >
                  ✨ Reload Now
                </button>
              </div>
            </div>
          </div>
        </aside>
      )}
    </>
  );
}

const styles: Record<string, CSSProperties> = {
  bannerWrap: {
    position: "fixed",
    top: 16,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 10050,
    width: "min(94vw, 560px)",
    pointerEvents: "auto",
  },
  bannerCard: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "12px 16px",
    borderRadius: 14,
    background: "linear-gradient(135deg, #241A12 0%, #15100C 100%)",
    border: "2px solid rgba(249, 175, 54, 0.7)",
    boxShadow: "0 10px 40px rgba(0,0,0,0.9), 0 0 25px rgba(249, 175, 54, 0.45)",
    animation: "stationPulseGlow 2.5s infinite ease-in-out",
    backdropFilter: "blur(12px)",
  },
  iconCircle: {
    width: 42,
    height: 42,
    borderRadius: "50%",
    background: "rgba(249, 175, 54, 0.15)",
    border: "1px solid rgba(249, 175, 54, 0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  rocketIcon: {
    fontSize: 22,
    display: "inline-block",
    animation: "stationRocketBob 2s infinite ease-in-out",
  },
  contentBlock: {
    flex: 1,
    minWidth: 0,
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 2,
    flexWrap: "wrap",
  },
  badge: {
    background: "#F9AF36",
    color: "#0D0B08",
    padding: "2px 6px",
    borderRadius: 4,
    fontSize: 9,
    fontWeight: 900,
    letterSpacing: 0.5,
  },
  titleText: {
    fontSize: 13,
    fontWeight: 800,
    color: "#FFF2DA",
  },
  subtitleText: {
    margin: 0,
    fontSize: 11,
    color: "rgba(255, 242, 218, 0.75)",
    lineHeight: 1.35,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  laterBtn: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid rgba(255, 255, 255, 0.15)",
    background: "rgba(255, 255, 255, 0.05)",
    color: "rgba(255, 242, 218, 0.7)",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "0.15s ease",
  },
  reloadBtn: {
    padding: "9px 16px",
    borderRadius: 8,
    border: "1px solid #F9AF36",
    background: "linear-gradient(135deg, #F9AF36 0%, #D4881A 100%)",
    color: "#0D0B08",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    whiteSpace: "nowrap",
    boxShadow: "0 2px 10px rgba(249, 175, 54, 0.4)",
    transition: "0.15s ease",
  },
  snoozePill: {
    position: "fixed",
    top: 14,
    right: 14,
    zIndex: 10050,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    borderRadius: 24,
    background: "#1E1610",
    border: "1.5px solid #F9AF36",
    boxShadow: "0 4px 20px rgba(0,0,0,0.8), 0 0 12px rgba(249, 175, 54, 0.35)",
    cursor: "pointer",
    fontSize: 11,
    animation: "stationPulseGlow 3s infinite ease-in-out",
  },
  snoozeDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#F9AF36",
    display: "inline-block",
    animation: "stationDotPulse 1.5s infinite ease-in-out",
  },
  snoozeHint: {
    color: "rgba(255, 242, 218, 0.6)",
    fontSize: 10,
    fontStyle: "italic",
  },
};

